import React, { useState, useEffect, useCallback, forwardRef, useRef, useMemo, useContext, memo } from "react";
import PropTypes from "prop-types";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import { Tooltip } from '@progress/kendo-react-tooltip';

import "./Detail.scss";
import DataGrid from "../dataGrid/DataGrid";
import MetaGrid from "../meta/MetaGrid";
import DropDownList from "../forms/DropDownList";
import Input from "../forms/Input";
import LoadingPanel from "../LoadingPanel";
import NumericTextBox from "../forms/NumericTextBox";
import UnitNumericTextBox from "../forms/UnitNumericTextBox";
import Picker from "../forms/Picker";
import AutocompleteComponent from "../forms/Autocomplete";
import MultiSelect from "../forms/MultiSelect";
import Switch from "../forms/Switch";
import Textarea from "../forms/Textarea";
import Conversational from "../conversational/Conversational";
import RadioGroup from "../forms/RadioGroup";
import Password from "../forms/Password";
import ButtonGroup from "../buttons/ButtonGroup";
import Toolbar from "./Toolbar";
import Upload from "../forms/Upload";
import ValidationMessage from "./ValidationMessage";
import ValidationAlert from "../ValidationAlert";
import FileContent from "../dialogs/FileContent";
import Checkbox from "../forms/Checkbox";
import MultiPicker from "../forms/MultiPicker";
import FileDialog from "../dialogs/FileDialog";
import FormDataTable from "../forms/FormDataTable";
import eventEmitter from '../utils/eventEmitter';
import withStorage from '../utils/withStorage';

// import DateInput from '../../test/DatePickerWrapper/DateInput';
import DateInput from "../forms/DateInput";
import DateRangeInput from '../forms/DateRangeInput';
import RCInput from "../forms/RCInput";
import DateTimeRangeInput from "../forms/DateTimeRangeInput";

import { ValidationContext } from "../utils/ValidationProvider";
import _ from "lodash";
import { DetailDataProvider, useDetailData } from "./DetailDataContext";
import withRequests from "../utils/withRequests";

const CustomComponent = memo(({ primaryField, x, onChange, schema, buildField, componentFactory, akc, parentProps }) => {
  const data = useDetailData();
  const value = useDetailData(x.field);
  const obj = {
    name: x.field,
    field: x,
    data,
    schema,
    buildField: buildField,
    akc,
    parentProps
  };
  return (
    <React.Fragment key={x.field}>
      {componentFactory
        ? componentFactory(
          primaryField,
          x,
          value,
          onChange,
          obj
        )
        : x.customComponent}
    </React.Fragment>
  );
});

const FileContentWrapper = withRequests(({ x, requests }) => {
  const value = useDetailData(x.field);
  const detailData = useDetailData();

  return <FileContent
    requests={requests}
    name={x.name || detailData[x.nameField]}
    data={x.data}
    url={x.url ? x.url.replace('{id}', detailData[x.idField] || 0) : null}
    mimeType={x.mimeType || detailData[x.mimeTypeField]}
    id={detailData[x.idField] || 0}
    hideButton={x.hideButton}
    imgWidth={x.imgWidth}
    pdfWidth={x.pdfWidth}
    pdfHeight={x.pdfHeight}
    src={value}
  />;
});

const FileDialogWrapper = withRequests(({ x, requests }) => {
  const detailData = useDetailData();
  <FileDialog
    isOpen={x.isOpen}
    requests={requests}
    name={x.name || detailData[x.nameField]}
    mimeType={x.mimeType || detailData[x.mimeTypeField]}
    id={detailData[x.idField]}
    imgWidth={x.imgWidth}
    pdfWidth={x.pdfWidth}
    pdfHeight={x.pdfHeight}
    src={detailData[x.field]}
  />;
});

const FormTableWrapper = ({ x, dataPrimaryField, parentProps, pageIndex, page, frms }) => {
  const detailData = useDetailData();
  return (
    <FormDataTable
      detailData={detailData}
      schema={x.schema}
      primaryField={x.identif}
      parentPrimaryField={dataPrimaryField}
      currentPage={pageIndex}
      myPage={page}
      frms={frms.find(y => y.IFrm === x.args?.[x.field]?.["IPodFrmNadr"]) ?? null}
      handleChangeFrm={parentProps.handleChangeFrm}
    />
  );
};

const DetailInput = memo(function DetailInput({ x, page, pageIndex, primaryField, onChange, onUpload, disabled, ref, handleBlur: propsHandleBlur, parentProps, withMeta, db, reducer, akcFactory, frms }) {
  const dataPrimaryField = primaryField.split("-").pop();
  const value = useDetailData(x.field);

  const handleBlur = useCallback((field, blurValue) => {
    const v = blurValue != null ? blurValue : value;
    propsHandleBlur(field, v);
  }, [value, propsHandleBlur]);

  switch (x.type) {
    case "files":
    case "fileupload":
      return (
        <Upload
          name={x.field}
          onChange={onChange}
          onUpload={x.onUpload || onUpload}
          value={value}
          disabled={x.disabled || disabled}
          url={x.url}
          restrictions={{
            allowedExtensions: x.restrictions != null ? x.restrictions : [],
            maxFileSize: x.maxFileSize,
          }}
          data={x.data}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          autoUpload={x.autoUpload}
          multiple={x.multiple}
          showActionButtons={x.showActionButtons}
          requiredMaximumSize={x.requiredMaximumSize}
          listItemUI={x.listItem}
        />
      );

    case "boolean":
      return (
        <Switch
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          onLabel={x.onLabel}
          offLabel={x.offLabel}
          ref={ref}
          tabIndex={x.tabIndex}
        />
      );
    case "checkbox":
      return (
        <>
          <span className={"detail-label"} />
          <Checkbox
            name={x.field}
            onChange={onChange}
            value={value}
            disabled={x.disabled || disabled}
            label={x.title}
            tabIndex={x.tabIndex}
          />
        </>
      );
    case "checkboxleft":
      return (
        <Checkbox
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          tabIndex={x.tabIndex}
        />
      );

    case "enum":
      return (
        <DropDownList
          name={x.field}
          data={
            parentProps[x.dataField]
              ? parentProps[x.dataField].data
              : []
          }
          textField={x.textField}
          dataItemKey={x.primaryField}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          valueRender={x.valueRender}
          itemRender={x.itemRender}
        />
      );

    case "buttongroup":
      let enumData;
      if (x.enumData) {
        enumData = x.enumData;
      } else if (parentProps[x.dataField]) {
        enumData = parentProps[x.dataField].data;
      } else {
        enumData = [];
      }

      return (
        <ButtonGroup
          name={x.field}
          data={enumData}
          textField={x.textField}
          dataItemKey={x.primaryField}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
        />
      );

    case "radio":
      return (
        <RadioGroup
          name={x.field}
          data={
            parentProps[x.dataField]
              ? parentProps[x.dataField].data
              : []
          }
          textField={x.textField}
          dataItemKey={x.primaryField}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          ref={ref}
          tabIndex={x.tabIndex}
          layout={x.layout}
        />
      );
    case "date":
    case "dateinput":
      return (
        <DateInput
          type="date"
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          min={x.min}
          max={x.max}
          format={x.format}
          //ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          key={x.field}
        />
      );
    case "datetime":
    case "datetimeinput":
      return (
        <DateInput
          type="datetime"
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          min={x.min}
          max={x.max}
          format={x.format}
          //ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          key={x.field}
        />
      );

    case "time":
    case "timeinput":
      return (
        <DateInput
          type="time"
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          min={x.min}
          max={x.max}
          format={x.format}
          steps={x.steps}
          //ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          key={x.field}
        />
      );

    case "daterange":
    case 'daterangeinput':
      return (
        <DateRangeInput
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          min={x.min !== null ? x.min : undefined}
          max={x.max !== null ? x.max : undefined}
          //ref={ref}
          tabIndex={x.tabIndex}
        />
      );

    case 'datetimerangeinput':
      return (
        <DateTimeRangeInput
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          min={x.min !== null ? x.min : undefined}
          max={x.max !== null ? x.max : undefined}
          ref={ref}
          tabIndex={x.tabIndex}
          hourRange={x.hourRange}
          steps={x.steps}
        />
      );

    case "numeric":
      return (
        <NumericTextBox
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          autofocus={x.autofocus}
          max={x.max}
          min={x.min}
          format={x.format}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          spinners={x.spinners}
          step={x.step}
        />
      );
    case "unitnumeric":
      const matches = x.title.match(/\[(.*)\]$/);
      const unit = matches && matches.length > 1 ? matches[1] : null;
      return (
        <UnitNumericTextBox
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          autofocus={x.autofocus}
          max={x.max}
          min={x.min}
          format={x.format}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          unit={unit}
          units={x.args?.units}
          notUi={true}
          spinners={x.spinners ? x.spinners : false}
          step={x.step}          
        />
      );

    case "container":
      return (
        <Picker
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          textField={x.textField}
          valueField={x.valueField || x.textField}
          onChange={parentProps[x.onChange]}
          onSubmit={parentProps[x.onSubmit]}
          data={
            parentProps[x.identif] ? parentProps[x.identif].data : []
          }
          isLoading={
            parentProps[x.identif] && parentProps[x.identif].isLoading
              ? parentProps[x.identif].isLoading
              : false
          }
          onSelect={onChange}
          selected={value}
          schema={x.schema}
          total={
            parentProps[x.identif] ? parentProps[x.identif].total : 0
          }
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          name={x.field}
          primaryField={x.primaryField}
          initFilter={x.initFilter}
          fixedFilter={x.fixedFilter}
          identif={x.identif}
          autofocus={x.autofocus}
          detail={x.detail}
          editableDetail={x.editableDetail}
          onAdd={parentProps[x.onAdd]}
          parentProps={parentProps}
          ref={ref}
          tabIndex={x.tabIndex}
          placeholder={x.placeholder}
          onBlur={() => handleBlur(x, null)}
          withMeta={withMeta}
          db={db}
          reducer={x.reducer || reducer}
          akcFactory={akcFactory}
          allDataItem={x.allDataItem}
          sort={x.sort}
          resetValue={parentProps?.resetValue ? x.args?.resetValue : false}
        />
      );

    // case "grid":
    //   return <Grid {...x} parentProps={props.parentProps} ref={ref} />;


    //case "basegrid":
    //  data = props.getValue(x.field);
    // return (
    //   <BaseGrid
    //     {...x}
    //     parentProps={props.parentProps}
    //     ref={ref}
    //     data={data}
    //     total={data ? data.length : 0}
    //   />
    // );
    case "grid":
    case "basegrid":
    case "datagrid":
      let data = value;
      return (
        <DataGrid
          {...x}
          disabled={x.disabledFlags}
          hidden={x.hiddenFlags}
          parentProps={parentProps}
          ref={ref}
          data={data}
          total={data ? data.length : 0}
        />
      );

    case "metagrid":
      return <MetaGrid {...x} parentProps={parentProps} ref={ref} />;

    case "text":
    case "textarea":
      return (
        <Textarea
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          autofocus={x.autofocus}
          ref={ref}
          tabIndex={x.tabIndex}
          minLength={x.minLength}
          maxLength={x.maxLength}
          onBlur={() => handleBlur(x, null)}
          defaultExpanded={(x.defaultExpanded !== undefined && x.defaultExpanded !== null) ? x.defaultExpanded : (x.type === "textarea" ? true : false)}
          readOnly={x.readOnly}
          richText={x.richText}
        />
      );

    case "29":
    case "rodné číslo":
      return (
        <RCInput
          name={x.field}
          type="sk"
          value={value}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          onChange={onChange}
          //ref={ref}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          tabIndex={x.tabIndex}
          autofocus={x.autofocus}
          onBlur={(value) => handleBlur(x, value)}
        />
      );

    case "autocomplete":
      return (
        <AutocompleteComponent
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          data={
            parentProps[x.dataField]
              ? parentProps[x.dataField].data
              : []
          }
          textField={x.textField}
          onSelect={parentProps[x.onSelect]}
          selected={value}
          disabled={x.disabled}
          required={x.required}
          recommended={x.recommended}
          onChange={parentProps[x.onChange]}
          ref={ref}
          primaryField={x.primaryField}
          tabIndex={x.tabIndex}
          isLoading={
            parentProps[x.data]
              ? parentProps[x.data].isLoading
              : false
          }
          onBlur={(value) => handleBlur(x, value)}
          valueField={x.valueField}
        />
      );

    case "multi":
      return (
        <MultiSelect
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          data={x.enumData ? x.enumData : parentProps[x.dataField]?.data}
          //total={props.parentProps[x.dataField].total}
          textField={x.textField}
          value={value}
          disabled={x.disabled}
          required={x.required}
          recommended={x.recommended}
          onChange={onChange}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          onFilterChange={x.onFilterChange}
        />
      );

    case "file":
    case "filepreview":
    case '26':
      return <FileContentWrapper x={x} />;

    case "chat":
      return (
        <Conversational
          user={x.user}
          messages={parentProps[x.messages] || []}
          width={x.width}
          onSend={x.onSend}
          placeholder={x.placeholder}
        />
      );

    case 'multipicker':
    case '25':
      return (
        <MultiPicker
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          textField={x.textField}
          valueField={x.valueField || x.textField}
          onChange={parentProps[x.onChange]}
          onSubmit={parentProps[x.onSubmit]}
          data={
            parentProps[x.identif] ? parentProps[x.identif].data : []
          }
          isLoading={
            parentProps[x.identif]
              ? parentProps[x.identif].isLoading
              : false
          }
          onSelect={onChange}
          selected={value}
          schema={x.schema}
          total={
            parentProps[x.identif] ? parentProps[x.identif].total : 0
          }
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          name={x.field}
          primaryField={x.primaryField}
          initFilter={x.initFilter}
          fixedFilter={x.fixedFilter}
          identif={x.identif}
          autofocus={x.autofocus}
          detail={x.detail}
          editableDetail={x.editableDetail}
          onAdd={parentProps[x.onAdd]}
          parentProps={parentProps}
          ref={ref}
          tabIndex={x.tabIndex}
          placeholder={x.placeholder}
          onBlur={() => handleBlur(x, null)}
          withMeta={withMeta}
          db={db}
          reducer={x.reducer}
          akcFactory={akcFactory}
        />
      );

    case 'filedialog':
    case '27':
      return (
        <FileDialogWrapper
          x={x}
        />
      );

    case "formdatatable":
    case "28":
      return (
        <FormTableWrapper
          x={x}
          dataPrimaryField={dataPrimaryField}
          parentProps={parentProps}
          pageIndex={pageIndex}
          page={page}
          frms={frms}
        />
      );

    case "password":
      return (
        <Password
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          isInvalid={x.isInvalid}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          autofocus={x.autofocus}
          minLength={x.minLength}
          maxLength={x.maxLength}
          pattern={x.pattern}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          copyable={x.copyable}
        />
      );

    case "string":
    case "email":
    default:
      return (
        <Input
          type={x.type}
          title={x.title}
          validationAllowed
          validationMessage={x.validationMessage}
          name={x.field}
          onChange={onChange}
          value={value}
          disabled={x.disabled || disabled}
          required={x.required}
          recommended={x.recommended}
          autofocus={x.autofocus}
          minLength={x.minLength}
          maxLength={x.maxLength}
          pattern={x.pattern}
          ref={ref}
          tabIndex={x.tabIndex}
          onBlur={() => handleBlur(x, null)}
          copyable={x.copyable}
          isInvalid={x.isInvalid}
        />
      );
  }
});

/**
 * @typedef {Object} detailSchema
 * @property {string} field - Unique field identifier
 * @property {string} title - Field name
 * @property {string} type - Field type - "string", "boolean", "enum", "date", "time", "datetime", "text", "numeric", "container", "files", "password", "fileupload", "filepreview"
 * @property {bool} required - Required
 * @property {bool} disabled - Readonly
 * @property {bool} autofocus - Show cursor in field?
 * @property {number} tabIndex - Index of the tab
 * @property {string} defaultValue - Default value
 * @property {string} validationMessage - Validation Message
 * @property {string} tooltip - Tooltip over the column
 * @property {bool} recommended - Recommended
 * @property {element} customComponent - Component displayed after the field.
 * @param {bool} validationIcon - Show validation icon?
 */

/**
 * Detail component
 * @module
 * @param {detailSchema[]} schema - Schema
 * @param {object} data - Data
 * @param {func} onChange - On field change function 
 * @param {func} onBlur - On field blur function
 * @param {func} handleSubmit - Handle sumbit function
 * @param {func} handleBlur - Invokes on field blur, function
 * @param {func} getValue - Get field value, function
 * @param {func} onUpload - On file upload function
 * @param {object} parentProps - Parent props
 * @param {bool} disabled - Read only detail
 * @param {number} akcDisabled - Disabled actions mask number
 * @param {number} akcHidden - Hidden actions mask number
 * @param {bool} isLoading - Is loading
 * @param {array} ui - Custom schema
 * @param {object} args - Custom arguments
 * @param {element} toolbarActions - Toolbar actions
 * @param {string} primaryField - Primary field
 * @param {bool} toolbarHidden - Toolbar hidden
 * @param {bool} autofocus - Show cursor in field?
 * @param {func} next - Switches to the next entry function
 * @param {func} previous - Switches to the previous entry function
 * @param {bool} disabledNext - Is next entry disabled?
 * @param {bool} disabledPrevious - Is previous entry disabled?
 * @param {element} currentEl - Current element
 * @param {func} onBack - Function invoked after click on back arrow
 * @param {object} initFilter - Init filter
 * @param {object} fixedFilter - Fixed filter
 * @param {func} onAdd - On entry add function
 * @param {func} onGet - Get item function
 * @param {bool} isPicker - Is field picker
 * @param {bool} hiddenBack - Hidden back arrow in detail toolbar
 * @param {func} applyFilter - Apply filter function
 * @param {func} componentFactory - Factory for custom components
 * @param {string} title - Detail title
 * @param {array} pages - Pages
 * @param {bool} hiddenSave - Hidden save button in detail toolbar
 * @param {bool} sidebarHorizontal - Should sidebar be horizontal?
 * @param {string} [db] - db
 */

const Detail = forwardRef((props, ref) => {
  const { schema, sidebarHorizontal, title, ui: propsUi, pages, openedSidepanels, pageTitle, toolbarActions, allowMultiple, componentFactory, data, akc, onChange, primaryField, handleBlur, onUpload, parentProps, disabled } = props;
  const dataPrimaryField = primaryField.split("-").pop();
  const [tabIndex, setTabIndex] = useState(0);
  const [pageIndex, setPageIndex] = useState(0);
  const [pagesOpened, setPagesOpened] = useState({});
  const tooltipRef = useRef(null);
  const [openTooltip, setOpenTooltip] = useState(false);
  const [tooltipElement, setTooltipElement] = useState(null);
  const [detailSize, setDetailSize] = useState(JSON.parse(sessionStorage.getItem('DetailSize')));
  const { getIsInvalid, getInvalidFieldsTitles } = useContext(ValidationContext);

  const getDetailSize = useCallback(async (newDetailSize) => {
    setDetailSize(newDetailSize || await props.storage.getDetailSize());
  }, [props.storage]);

  const getPageOffset = useCallback(() => {
    return pages && pages.find((x) => x.IZalTyp === 2) ? 0 : 1;
  }, [pages]);

  const buildPageTitle = useCallback((pageIndex) => {
    const offset = getPageOffset();
    if (pages && pages[pageIndex - offset]) {
      return pages[pageIndex - offset].title;
    }

    switch (pageIndex) {
      case 1:
        return "Doplňujúce informácie";
      case 2:
        if (pageTitle) {
          return pageTitle;
        } else {
          return "Workflow";
        }
      default:
        return "Neznámy index strany";
    }
  }, [pages, pageTitle, getPageOffset]);

  const buildPageCustomClass = useCallback((pageIndex) => {
    const offset = getPageOffset();
    if (pages && pages[pageIndex - offset]) {
      const customClass = pages[pageIndex - offset].customClass;
      return customClass || "";
    }
    return "";
  }, [getPageOffset, pages]);

  const ui = useMemo(() => {
    ;
    const offset = getPageOffset();

    if (openedSidepanels) {
      setPagesOpened(openedSidepanels.reduce((o, value) => Object.assign(o, { [`page-${value}`]: true }), {}));
    }

    return propsUi
      ? propsUi.map((page, pageIndex) => {
        return {
          page,
          title: buildPageTitle(pageIndex),
          customClass: buildPageCustomClass(pageIndex),
          IZobraz: offset === 1 ? (pages && pages[pageIndex - offset] ? pages[pageIndex - offset].IZobraz : 1) : pages[pageIndex].IZobraz,
          IZalTyp: offset === 1 ? 1 : pages[pageIndex].IZalTyp,
        };
      })
      : null;
  }, [propsUi, pages, openedSidepanels, buildPageCustomClass, buildPageTitle, getPageOffset]);

  useEffect(() => {
    let newIndex = 0;
    if (pages) {
      newIndex = pages.findIndex((page) => page.active) + 1;
    }
    setPageIndex(newIndex);
  }, [pages]);

  useEffect(() => {
    if (!detailSize) {
      getDetailSize();
    }

    eventEmitter.on('CHANGE_DETAIL_SIZE', getDetailSize);
    return () => {
      eventEmitter.removeListener('CHANGE_DETAIL_SIZE', getDetailSize);
    };
  }, [detailSize, getDetailSize]);

  const handleClick = useCallback((event, pageIndexNew) => {
    setPageIndex((prev) => prev === pageIndexNew ? 0 : pageIndexNew);
  }, []);

  const handleClickMultiple = useCallback((event, newPageIndex) => {
    setPagesOpened(prevState => ({ ...prevState, [`page-${newPageIndex}`]: !pagesOpened[`page-${newPageIndex}`] }));
  }, [pagesOpened]);

  const handleSelect = (event) => {
    setTabIndex(event.selected);
  };

  const buildField = useCallback((x, fieldClassName, page) => {
    if (x.hidden && !(["basegrid", "datagrid", "metagrid"].includes(x.type) && _.isNil(x.hiddenFlags))) {
      return null;
    }
    const disabledClass = x.disabled ? "disabled-field" : "";
    let title = x.title;
    if (x.type === "unitnumeric") {
      const matches = x.title.match(/(.*)\[(.*)\]$/);
      title = matches && matches.length > 1 ? matches[1] : null;
    }

    const invalidField = getIsInvalid(x.field);
    const customClass = `${fieldClassName || ""} ${invalidField?.isInvalid ? "validation-invalid" : (invalidField?.isRecommendedInvalid ? "recommended-invalid" : "validation-valid")}`.trim();

    return (
      <React.Fragment key={x.field + (x.identif ?? "")}>
        <div
          title={x.tooltip}
          className={
            x.required && !x.disabled
              ? `k-form-field required form-${x.type} form-field-${x.field} ${customClass}`
              : `k-form-field form-${x.type === "text" ? "text-input" : x.type} form-field-${x.field} ${disabledClass} ${customClass}`
          }
          onMouseOver={(e) => { setOpenTooltip(true); setTooltipElement(e.currentTarget); }}
          onMouseOut={(e) => { setOpenTooltip(false); setTooltipElement(null); }}
        >
          {x.type === "checkbox" || x.type === "formdatatable" ? null : (
            <span className="detail-label">
              {x.validationIcon && (
                <span className={`k-icon k-font-icon validation-image`}></span>
              )}
              {title}
            </span>
          )}
          <DetailInput x={x} page={page} pageIndex={pageIndex} primaryField={primaryField} onChange={onChange} onUpload={onUpload} disabled={disabled} ref={ref} handleBlur={handleBlur} parentProps={parentProps} withMeta={props.withMeta} db={props.db} reducer={props.reducer} akcFactory={props.akcFactory} frms={props.frms} />
          {x.tooltip && <div className="detail-tooltip">{x.tooltip}</div>}
          {x.customComponent}
        </div>
        {invalidField &&
          <ValidationMessage
            required={x.required}
            recommended={x.recommended}
            disabled={x.disabled}
            validationMessage={invalidField?.validationMessage}
          />
        }
      </React.Fragment>
    );
  }, [getIsInvalid, disabled, handleBlur, onChange, onUpload, pageIndex, parentProps, primaryField, props.akcFactory, props.db, props.reducer, props.withMeta, props.frms, ref]);

  const buildPageClassName = useCallback((pageIndexNew) => {
    if (pageIndexNew > 0) {
      if ((!allowMultiple && pageIndex === pageIndexNew) || (allowMultiple && pagesOpened[`page-${pageIndexNew}`])) {
        const scrollableElement =
          document.getElementsByClassName("scrollable-navbar")[0];
        return `sidebar-${pageIndexNew} col-4 ${sidebarHorizontal ? "sidebar-horizontal" : ""
          } ${scrollableElement &&
            scrollableElement.scrollHeight > scrollableElement.clientHeight
            ? ""
            : "scrollable-expanded-page"
          }`;
      } else {
        return `sidebar-${pageIndexNew} compact col-1 ${sidebarHorizontal ? "sidebar-horizontal" : ""
          }`;
      }
    } else {
      return "page col";
    }
  }, [allowMultiple, sidebarHorizontal, pageIndex, pagesOpened]);

  const buildColumnClassName = useCallback((column) => {
    return column[0] && column[0].className
      ? `col-md ${column[0].className}`
      : "col-md";
  }, []);

  const buildLine = useCallback((field) => {
    return <div className={`detail-line-${field.line}`}><hr /></div>;
  }, []);

  const buildPage = useCallback((page, index) => {
    return page.page.map((row, rowIndex) => (
      <div key={rowIndex} className="row">
        {row.map((column, columnIndex) => (
          <div key={columnIndex} className={buildColumnClassName(column)}>
            {column.map((field) => {
              if (field.customComponent) {
                return <CustomComponent x={field} onChange={onChange} schema={props.schema} buildField={buildField} primaryField={primaryField} akc={akc} componentFactory={componentFactory} parentProps={parentProps}/>;
              } else if (field.title) {
                return <div className="detail-subtitle">{field.title}</div>;
              } else if (field.line) {
                return buildLine(field);
              } else {
                let x = props.schema.find((f) => (f.field === field.field && (field.data !== undefined ? f.data === field.data : true)));
                return x
                  ? buildField(
                    x,
                    [field.className, x.className].filter((s) => s).join(" "),
                    index
                  )
                  : null;
              }
            })}
          </div>
        ))}
      </div>
    ));
  }, [buildColumnClassName, buildField, buildLine, props.schema, onChange, primaryField, akc, componentFactory]);

  const buildSideBarItem = useCallback((pageIndexNew) => {
    const iconOpen = sidebarHorizontal ? "down" : "left";
    const iconClose = sidebarHorizontal ? "up" : "right";
    return (
      pageIndexNew > 0 && (
        <span
          className="sidebar-header"
          onClick={(event) => allowMultiple ? handleClickMultiple(event, pageIndexNew) : handleClick(event, pageIndexNew)}
        >
          <h2>{buildPageTitle(pageIndexNew)}</h2>
          <span
            className={`sidebar-toggle k-icon k-font-icon ${(!allowMultiple && pageIndex !== pageIndexNew) || (allowMultiple && !pagesOpened[`page-${pageIndexNew}`])
              ? "compact k-i-arrow-chevron-" + iconOpen
              : "k-i-arrow-chevron-" + iconClose
              }`}
          />
        </span>
      )
    );
  }, [buildPageTitle, handleClick, handleClickMultiple, pageIndex, pagesOpened, allowMultiple, sidebarHorizontal]);

  const buildTabsForm = useCallback(() => {
    const pages = ui.filter((page) => page.IZobraz !== 3);
    return (
      <div className="detail">
        <TabStrip
          selected={tabIndex}
          onSelect={handleSelect}
          tabPosition={props.args?.tabPosition}
          className={"page col"}
        >
          {pages.map((page, pageIndex) => {
            return (
              page.IZalTyp === 2 && (
                <TabStripTab
                  key={pageIndex}
                  title={page.title}
                  disabled={page.IZobraz === 2}
                >
                  <div className="detail row">{buildPage(page, pageIndex)}</div>
                </TabStripTab>
              )
            );
          })}
        </TabStrip>
        {pages.map((page, pageIndex) => {
          return (
            page.IZalTyp === 1 && (
              <div
                key={pageIndex}
                className={
                  buildPageClassName(pageIndex) + " " + buildPageCustomClass(pageIndex)
                }
              >
                {buildSideBarItem(pageIndex)}
                {buildPage(page, pageIndex)}
              </div>
            )
          );
        })}
      </div>
    );
  }, [buildPage, buildPageClassName, buildPageCustomClass, buildSideBarItem, props.args?.tabPosition, tabIndex, ui]);

  const buildDefaultForm = useCallback(() => {
    return ui.filter(page => page.IZobraz !== 3).map((page, pageIndex) => (
      <div
        key={pageIndex}
        className={
          buildPageClassName(pageIndex) + " " + buildPageCustomClass(pageIndex)
        }
      >
        {buildSideBarItem(pageIndex)}
        {title && pageIndex === 0 && (
          <div className="row">
            <div className="col-md">
              <div className="detail-title">{title}</div>
            </div>
          </div>
        )}
        {buildPage(page, pageIndex)}
      </div>
    ));
  }, [buildPage, buildPageClassName, buildPageCustomClass, buildSideBarItem, title, ui]);

  const buildHorizontalForm = useCallback(() => {
    const sidebarColumns = ui.filter((page, pageIndex) => pageIndex > 0);
    const uiColumns = ui.filter((page, pageIndex) => pageIndex === 0);

    let form = [
      uiColumns.map((page, index) => (
        <div
          key={index}
          className={
            buildPageClassName(index) +
            " " +
            buildPageCustomClass(index)
          }
        >
          {title && index === 0 && (
            <div className="row">
              <div className="col-md">
                <div className="detail-title">{title}</div>
              </div>
            </div>
          )}
          {buildPage(page, index)}
        </div>
      )),
      <div className={`side-column ${pageIndex ? `column-expanded` : ``}`}>
        {sidebarColumns.map((page, index) => (
          <div
            key={index}
            className={
              buildPageClassName(index + 1) +
              " " +
              buildPageCustomClass(index + 1)
            }
          >
            {buildSideBarItem(index + 1)}
            {buildPage(page, index)}
          </div>
        ))}
      </div>,
    ];
    return form;
  }, [buildPage, buildPageClassName, buildPageCustomClass, buildSideBarItem, pageIndex, title, ui]);

  const buildForm = useCallback(() => {
    if (ui) {
      if (ui && ui.find((x) => x.IZalTyp === 2)) {
        return buildTabsForm();
      } else {
        return (
          <div className="detail">
            {sidebarHorizontal
              ? buildHorizontalForm()
              : buildDefaultForm()}
          </div>
        );
      }
    } else {
      return (
        <div className="detail">
          <div className="col-md page">
            {title && <div className="row"><div className="col-md"> <div className="detail-title">{title}</div></div></div>}
            <div className="row">
              {schema.map((x) => buildField(x, x.className))}
            </div>
          </div>
        </div>
      );
    }
  }, [ui, title, schema, sidebarHorizontal, buildTabsForm, buildHorizontalForm, buildDefaultForm, buildField]);

  const form = useMemo(() => buildForm(), [buildForm]);

  const toolbarActionsComponent = useMemo(() => typeof toolbarActions === "function" ? toolbarActions() : toolbarActions, [toolbarActions]);
  return (
    <div className={`detail-wrapper ${detailSize ? detailSize.class : ''}`}>
      <ValidationAlert
        dataInvalid={props.dataInvalid}
        invalidFields={getInvalidFieldsTitles()}
      />
      {!props.toolbarHidden && (
        <Toolbar
          disabled={props.disabled}
          akcDisabled={props.akcDisabled}
          akcHidden={props.akcHidden}
          primaryField={dataPrimaryField}
          previous={props.previous}
          next={props.next}
          disabledNext={props.disabledNext}
          disabledPrevious={props.disabledPrevious}
          currentEl={props.currentEl}
          onBack={props.onBack}
          isPicker={props.isPicker}
          onGet={props.onGet}
          onSubmit={props.handleSubmit}
          hiddenBack={props.hiddenBack}
          hiddenSave={props.hiddenSave}
          hiddenPrint={props.primaryField !== "ZiadPrist-I_ZIADOST"}
          onPrint={props.onPrint}
          withMeta={props.withMeta}
        >
          {toolbarActionsComponent}
        </Toolbar>
      )}
      <DetailDataProvider state={data}>
        {!props.isPicker ? (
          <form id={dataPrimaryField} className="k-form-inline" >
            {props.schema && form}
            {props.children}
            <LoadingPanel isLoading={props.isLoading} showAbort={props.showAbort} />
          </form>
        ) : (
          <div id={dataPrimaryField} className="k-form-inline">
            {props.schema && form}
            {props.children}
            <LoadingPanel isLoading={props.isLoading} showAbort={props.showAbort} />
          </div>
        )}
      </DetailDataProvider>
      <Tooltip
        ref={tooltipRef}
        position={"top"}
        open={openTooltip}
        targetElement={tooltipElement}
        anchorElement={"target"}
        parentTitle={false}
      />
    </div>
  );
});

Detail.propTypes = {
  schema: PropTypes.array,
  data: PropTypes.object,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleBlur: PropTypes.func,
  getValue: PropTypes.func,
  onUpload: PropTypes.func,
  parentProps: PropTypes.object,
  frms: PropTypes.array,
  disabled: PropTypes.bool,
  akcDisabled: PropTypes.number,
  akcHidden: PropTypes.number,
  isLoading: PropTypes.bool.isRequired,
  ui: PropTypes.array,
  args: PropTypes.object,
  toolbarActions: PropTypes.any,
  primaryField: PropTypes.string.isRequired,
  toolbarHidden: PropTypes.bool,
  autofocus: PropTypes.bool,
  next: PropTypes.func.isRequired,
  previous: PropTypes.func.isRequired,
  disabledNext: PropTypes.bool,
  disabledPrevious: PropTypes.bool,
  currentEl: PropTypes.element,
  onBack: PropTypes.func.isRequired,
  onPrint: PropTypes.func,
  initFilter: PropTypes.object,
  fixedFilter: PropTypes.object,
  onAdd: PropTypes.func,
  onGet: PropTypes.func,
  isPicker: PropTypes.bool,
  hiddenBack: PropTypes.bool,
  applyFilter: PropTypes.func,
  title: PropTypes.string,
  pages: PropTypes.arrayOf(PropTypes.object),
  hiddenSave: PropTypes.bool,
  sidebarHorizontal: PropTypes.bool,
  withMeta: PropTypes.bool,
  componentFactory: PropTypes.func,
  db: PropTypes.string,
};

export default withStorage(Detail);